import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// Import necessary icons and components
import { CheckCircle, Clock, AlertTriangle, ChevronRight, Download } from 'lucide-react';
import { Select, Button, Dropdown, Menu, Space } from 'antd'; // Added Button, Dropdown, Menu, Space
import { getObservations, getAreas } from '../../services/observationAPI'; // Assuming these are correctly implemented
import toast from 'react-hot-toast';

// Import report generation libraries
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType } from 'docx';
import { saveAs } from 'file-saver';

const MyActions = () => {
    // Existing State
    const [observations, setObservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // Status filter
    const [areas, setAreas] = useState([]);
    const [selectedArea, setSelectedArea] = useState(''); // Area filter (stores area ID)

    // --- New State ---
    // Map to store area names by ID for efficient lookup in reports
    const [areaMap, setAreaMap] = useState({});

    const navigate = useNavigate();

    // Fetch observations and areas
    const fetchData = useCallback(async () => {
        setLoading(true); // Ensure loading is true at the start
        try {
            // Fetch observations and areas data (consider Promise.all for parallel fetching)
            const observationsData = await getObservations();
            const areasData = await getAreas();

            // Ensure data received is an array, provide fallback
            const fetchedObservations = observationsData?.observations || [];
            const fetchedAreas = areasData?.data || [];

            setObservations(fetchedObservations);
            setAreas(fetchedAreas);

            // --- New: Populate Area Map ---
            // Create a map of { areaId: areaName }
            const map = fetchedAreas.reduce((acc, area) => {
                if (area._id && area.name) { // Ensure area has id and name
                    acc[area._id] = area.name;
                }
                return acc;
            }, {});
            setAreaMap(map);

        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
            // Reset state on error to avoid issues
            setObservations([]);
            setAreas([]);
            setAreaMap({});
        } finally {
            setLoading(false);
        }
    }, []); // Empty dependency array: fetchData is created once

    useEffect(() => {
        fetchData();
    }, [fetchData]); // Run effect when fetchData changes (which is only on mount here)

    // --- New: Helper Function to Get Area Name ---
    const getAreaNameById = useCallback((areaId) => {
        return areaMap[areaId] || 'Unknown Area'; // Return name or fallback
    }, [areaMap]); // Re-create only if areaMap changes

    // Filter observations based on status and area
    const filteredObservations = observations.filter(observation => {
        // Match status filter ('all' or specific status)
        const statusMatch = filter === 'all' || observation.status === filter;
        // Match area filter ('all' selectedArea='' or specific area ID)
        // Ensure observation.area exists before comparing
        const areaMatch = !selectedArea || (observation.area && observation.area._id === selectedArea);
        // Return true only if both conditions match
        return statusMatch && areaMatch;
    });

    // --- Report Generation Functions ---

    // Prepare data for reports based on current filters
    const prepareReportData = useCallback(() => {
        return filteredObservations.map(obs => ({
            description: obs.description || 'N/A',
            // Use the helper function to get the area name from the ID
            area: getAreaNameById(obs.area),
            // Define what 'Action' means in your context.
            // Using description as a placeholder. Replace with actual action field if available.
            action: obs.actionTaken || obs.description || 'N/A', // Example: Use actionTaken field if exists
            // Format status for readability
            status: obs.status ? obs.status.charAt(0).toUpperCase() + obs.status.slice(1) : 'N/A',
        }));
    }, [filteredObservations, getAreaNameById]); // Dependencies: re-run if filters or area names change

    // Generate PDF Report
    const generatePdf = useCallback(() => {
        if (filteredObservations.length === 0) {
            toast.error("No data to export for the current filter.");
            return;
        }
        const doc = new jsPDF();
        const tableData = prepareReportData();
        // Define table columns matching the required output
        const tableColumn = ["Observation Description", "Area", "Action", "Response Status"];
        // Map data to rows
        const tableRows = tableData.map(item => [
            item.description,
            item.area,
            item.action,
            item.status
        ]);

        // Add title to the PDF
        doc.text("Observations Report", 14, 15);
        // Use jspdf-autotable to generate the table
        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20, // Position table below the title
            headStyles: { fillColor: [22, 160, 133] }, // Example header style
            alternateRowStyles: { fillColor: [240, 240, 240] }, // Example row style
        });

        // Trigger download
        doc.save("observations_report.pdf");
        toast.success("PDF report downloaded!");
    }, [prepareReportData, filteredObservations.length]); // Dependencies

    // Generate Excel Report
    const generateExcel = useCallback(() => {
        if (filteredObservations.length === 0) {
            toast.error("No data to export for the current filter.");
            return;
        }
        const tableData = prepareReportData();
        // Map data keys to desired Excel column headers
        const worksheetData = tableData.map(item => ({
            "Observation Description": item.description,
            "Area": item.area,
            "Action": item.action,
            "Response Status": item.status
        }));

        // Create worksheet and workbook
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Observations"); // Sheet name

        // Optional: Adjust column widths
        // Example: [{wch: ColumnWidthInCharacters}, ...]
        worksheet["!cols"] = [ { wch: 40 }, { wch: 20 }, { wch: 40 }, { wch: 15 }];

        // Trigger download
        XLSX.writeFile(workbook, "observations_report.xlsx");
        toast.success("Excel report downloaded!");
    }, [prepareReportData, filteredObservations.length]); // Dependencies

    // Generate Word Report
    const generateWord = useCallback(async () => {
        if (filteredObservations.length === 0) {
            toast.error("No data to export for the current filter.");
            return;
        }
        try {
            const tableData = prepareReportData();
            const columnHeaders = ["Observation Description", "Area", "Action", "Response Status"];

            // Create header row with bold text
            const headerRow = new TableRow({
                children: columnHeaders.map(header => new TableCell({
                     children: [new Paragraph({ children: [new TextRun({ text: header, bold: true })] })],
                     // Optional: Add shading to header cells
                     // shading: { fill: "E0E0E0" },
                })),
                 // Ensure header row repeats on new pages if table breaks
                 tableHeader: true,
            });

            // Create data rows
            const dataRows = tableData.map(item => new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph(item.description)] }),
                    new TableCell({ children: [new Paragraph(item.area)] }),
                    new TableCell({ children: [new Paragraph(item.action)] }),
                    new TableCell({ children: [new Paragraph(item.status)] }),
                ],
            }));

            // Create the table with specified width
            const table = new Table({
                 rows: [headerRow, ...dataRows],
                 width: { size: 100, type: WidthType.PERCENTAGE }, // Use full page width
            });

            // Create the document structure
            const doc = new Document({
                sections: [{
                    properties: {},
                    children: [
                        // Add a title
                        new Paragraph({
                            children: [new TextRun({ text: "Observations Report", bold: true, size: 28 })], // size in half-points
                            spacing: { after: 200 }, // Add space after the title
                        }),
                        // Add the table
                        table,
                    ],
                }],
            });

            // Pack the document into a blob and trigger download
            const blob = await Packer.toBlob(doc);
            saveAs(blob, "observations_report.docx");
            toast.success("Word report downloaded!");
        } catch (error) {
            console.error("Error generating Word document:", error);
            toast.error("Failed to generate Word report.");
        }
    }, [prepareReportData, filteredObservations.length]); // Dependencies

    // --- End Report Generation Functions ---


    // Get status color (Existing function - unchanged)
    const getStatusColor = (status) => {
        switch (status) {
            case 'waiting': return 'text-amber-500';
            case 'in-progress': return 'text-purple-500';
            case 'submitted': return 'text-blue-500';
            case 'completed': return 'text-green-500';
            default: return 'text-gray-500';
        }
    };

    // Get status icon (Existing function - unchanged)
    const getStatusIcon = (status) => {
        switch (status) {
            case 'waiting': return <Clock className="h-5 w-5" />;
            case 'in-progress': return <AlertTriangle className="h-5 w-5" />;
            case 'submitted':
            case 'completed': return <CheckCircle className="h-5 w-5" />;
            default: return null;
        }
    };

    // Handle action click (Existing function - unchanged)
    const handleActionClick = (observationId, status) => {
        switch (status) {
            case 'waiting': navigate(`/my-action/waiting/${observationId}`); break;
            case 'in-progress': navigate(`/my-action/in-progress/${observationId}`); break;
            case 'submitted': navigate(`/my-action/submitted/${observationId}`); break;
            case 'completed': navigate(`/my-action/completed/${observationId}`); break;
            default: navigate(`/employee/review-action/${observationId}`);
        }
    };

    // --- New: Define Download Menu ---
    const downloadMenu = (
        <Menu onClick={({ key }) => { // Handle menu item clicks
            if (key === 'pdf') generatePdf();
            else if (key === 'excel') generateExcel();
            else if (key === 'word') generateWord();
        }}>
            <Menu.Item key="pdf">Download as PDF</Menu.Item>
            <Menu.Item key="excel">Download as Excel</Menu.Item>
            <Menu.Item key="word">Download as Word</Menu.Item>
        </Menu>
    );

    // Loading State UI (Existing - unchanged)
    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    // Main Component Render
    return (
        // Added padding for better spacing
        <div className="space-y-6 p-4 md:p-6">
            {/* Header Section: Title and Download Button */}
             <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                {/* Title and Subtitle */}
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">My Actions</h1>
                    <p className="text-gray-600">View, manage, and report your assigned actions.</p> {/* Updated text */}
                </div>

                {/* --- New: Download Button --- */}
                <Dropdown
                    overlay={downloadMenu} // The menu defined above
                    placement="bottomRight"
                    // Disable button if no filtered data exists
                    disabled={filteredObservations.length === 0}
                >
                    {/* Ant Design Button with Icon */}
                    <Button type="primary" icon={<Download size={18} />} className="flex items-center">
                        Download Report
                    </Button>
                </Dropdown>
             </div>


            {/* Filter Section: Area and Status */}
            {/* Added some styling for visual separation */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-start sm:items-center bg-gray-50 p-4 rounded-md shadow-sm">
                {/* Area Filter Select (Existing Structure) */}
                 <div className="flex items-center gap-2">
                     <label htmlFor="area-filter" className="font-medium text-gray-700 text-sm">Area:</label>
                     <Select
                         id="area-filter"
                         value={selectedArea}
                         onChange={(value) => setSelectedArea(value)}
                         style={{ width: 180 }}
                         showSearch // Enable search
                         placeholder="Filter by Area"
                         allowClear // Allow user to clear selection
                         // Improve search: match case-insensitively
                         filterOption={(input, option) =>
                             (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                         }
                     >
                         <Select.Option value="">All Areas</Select.Option>
                         {/* Populate options from fetched areas */}
                         {areas.map(area => (
                             <Select.Option key={area._id} value={area._id}>
                                 {area.name}
                             </Select.Option>
                         ))}
                     </Select>
                 </div>

                {/* Status Filter Buttons (Existing Structure) */}
                 {/* Added label and minor style adjustments */}
                 <div className="flex items-center gap-2 flex-wrap">
                     <span className="font-medium text-gray-700 text-sm">Status:</span>
                     <div className="flex flex-wrap space-x-2 gap-2">
                         {/* Using map for cleaner button generation could be an option, but keeping existing structure */}
                         <button
                             onClick={() => setFilter('all')}
                             // Adjusted padding/text size for consistency
                             className={`px-3 py-1 text-sm rounded font-medium transition-colors ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                         >
                             All
                         </button>
                         <button
                             onClick={() => setFilter('waiting')}
                             className={`px-3 py-1 text-sm rounded font-medium transition-colors ${filter === 'waiting' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                         >
                             Waiting
                         </button>
                         <button
                             onClick={() => setFilter('in-progress')}
                              // Using purple color defined in getStatusColor
                             className={`px-3 py-1 text-sm rounded font-medium transition-colors ${filter === 'in-progress' ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                         >
                             In Progress
                         </button>
                         <button
                             onClick={() => setFilter('submitted')}
                             className={`px-3 py-1 text-sm rounded font-medium transition-colors ${filter === 'submitted' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                         >
                             Submitted
                         </button>
                         <button
                             onClick={() => setFilter('completed')}
                             className={`px-3 py-1 text-sm rounded font-medium transition-colors ${filter === 'completed' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                         >
                             Completed
                         </button>
                     </div>
                 </div>
            </div>

            {/* Actions List (Existing Structure - Minor style tweaks) */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <ul className="divide-y divide-gray-200">
                    {filteredObservations.length === 0 ? (
                        // Message when no observations match filters
                        <li className="px-6 py-10 text-center text-gray-500"> {/* Increased padding */}
                            No actions found matching the selected filters.
                        </li>
                    ) : (
                        // Map through filtered observations
                        filteredObservations.map(observation => (
                            <li
                                key={observation._id}
                                // Added transition for smoother hover effect
                                className="px-4 sm:px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                                onClick={() => handleActionClick(observation._id, observation.status)}
                                // Accessibility improvements
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleActionClick(observation._id, observation.status)}}
                            >
                                <div className="flex items-center justify-between">
                                    {/* Main content area */}
                                    {/* Added min-w-0 to prevent text overflow issues in flex children */}
                                    <div className="flex-1 min-w-0 pr-4">
                                        <div className="flex items-start sm:items-center gap-3">
                                            {/* Status Icon */}
                                            <span className={`flex-shrink-0 ${getStatusColor(observation.status)}`}>
                                                {getStatusIcon(observation.status)}
                                            </span>
                                            {/* Observation Details */}
                                            <div className="flex-1 min-w-0">
                                                {/* Description - Added truncate */}
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {observation.description || 'No Description'}
                                                </p>
                                                {/* Meta Info: Status Badge & Date */}
                                                {/* Adjusted layout and styling for clarity */}
                                                <div className="mt-1 flex flex-wrap items-center text-xs sm:text-sm text-gray-500 gap-x-3 gap-y-1">
                                                     {/* Status Badge - More distinct styling */}
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-medium ${getStatusColor(observation.status)} bg-opacity-10 ${
                                                        observation.status === 'waiting' ? 'bg-amber-100' :
                                                        observation.status === 'in-progress' ? 'bg-purple-100' :
                                                        observation.status === 'submitted' ? 'bg-blue-100' :
                                                        observation.status === 'completed' ? 'bg-green-100' : 'bg-gray-100'
                                                        }`}>
                                                        {observation.status ? observation.status.charAt(0).toUpperCase() + observation.status.slice(1) : 'Unknown'}
                                                    </span>
                                                    {/* Separator */}
                                                    {/* <span className="text-gray-300 hidden sm:inline">•</span> */}
                                                    {/* Area Name - Using helper function */}
                                                    <span className="inline-flex items-center">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                          <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        </svg>
                                                        {getAreaNameById(observation.area._id)}
                                                    </span>
                                                    {/* Creation Date */}
                                                    <span className="inline-flex items-center">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        Created: {new Date(observation.createdAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {/* Rejection Reason (Existing - unchanged) */}
                                                {observation.rejectionReason && (
                                                    <p className="mt-1 text-sm text-red-600">
                                                        Rejection reason: {observation.rejectionReason}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Chevron Icon - Added flex-shrink-0 */}
                                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
                                </div>
                            </li>
                        ))
                    )}
                </ul>
            </div>
        </div>
    );
};

export default MyActions;
